import React, { useCallback, useLayoutEffect } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { HEIGHT } from 'design-system/Button';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import StatusIcon from 'design-system/StatusIcon';
import LogoBankAccount from 'design-system/LogoBankAccount';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { AppView, Button, ButtonGroup, CurrencyAmountText, rem, SCROLL_INDICATOR_INSETS, Spacer } from 'design-system';
import Links from 'utils/links';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { formatDateInProgressTimeline as formatTimeInfoDate } from 'features/invest/utils';
import ProgressCard from '../components/InvestOnboardingFunnel/ProgressCard';
import { isWeb } from '../../../constants';
const WithdrawFromAccountTimeline = withAnimatedNavigationHeader(({ scrollY, navigation, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => (<NavigationCloseButton inWebHeader onPress={() => {
                    navigation.pop();
                }}/>),
            headerBackVisible: false,
        });
    }, []);
    const { connectedAccount, date, amount } = route.params;
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT + rem(16), undefined, paddingHorizontalStyle);
    const handleClose = useCallback(() => {
        navigation.goBack();
    }, []);
    const handlePress = useCallback(() => {
        Links.openLink('https://emma-app.com/learn-more/withdrawal-from-seccl');
    }, []);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} keyboardDismissMode="on-drag" contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <Spacer h={24}/>
          <View style={styles.amountInfo}>
            <View>
              <View style={styles.iconContainer}>
                <LogoBankAccount data={connectedAccount} size="R64"/>
              </View>
              <View style={styles.statusIconContainer}>
                <Squircle width={rem(24)} height={rem(24)} color={colors.cards.onDark}>
                  <StatusIcon state={'in-progress'} height={20} width={20}/>
                </Squircle>
              </View>
            </View>
            <Spacer h={24}/>
            <Text Text-14 Secondary>
              Withdrawal request
            </Text>
            <CurrencyAmountText size="XL" value={{ amount, currency: 'GBP' }}/>
          </View>
          <Spacer h={24}/>
          <ProgressCard lightBg completed noTextDecoration alwaysShowTimeline title="Withdrawal requested" timeInfo={formatTimeInfoDate(date)}/>
          <ProgressCard lightBg lastItem isActiveStep completed={false} title={connectedAccount.bankName || 'Linked bank'}>
            <Text TextThin-14 Secondary>
              Your withdrawal is being processed and should reach your bank account within 3 working days.{' '}
              <Text Primary Text-14 suppressHighlighting onPress={handlePress}>
                Learn more
              </Text>
            </Text>
          </ProgressCard>
          <Spacer h={24}/>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button title="Close" brand onPress={handleClose}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    title: ``,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    amountInfo: {
        alignItems: 'center',
    },
    iconContainer: {
        width: rem(64),
        height: rem(64),
        borderRadius: rem(64),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.cards.onDark,
    },
    statusIconContainer: {
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
}));
export default WithdrawFromAccountTimeline;
